
import { formatPercentChange, formatVolume, formatMarketCap } from '../utils/format-helpers';

// Get top price movers (biggest gainers and losers)
export function getTopMovers(marketData, limit = 5) {
  if (!marketData || marketData.length === 0) {
    return {
      gainers: [],
      losers: []
    };
  }

  // Filter out coins with null price changes
  const validData = marketData.filter(coin => 
    coin.price_change_percentage_24h !== null && 
    coin.price_change_percentage_24h !== undefined
  );

  // Sort by 24h price change percentage
  const sortedByGain = [...validData].sort((a, b) => 
    (b.price_change_percentage_24h || 0) - (a.price_change_percentage_24h || 0)
  );

  const gainers = sortedByGain.slice(0, limit).map(coin => ({
    id: coin.id,
    name: coin.name,
    symbol: coin.symbol.toUpperCase(),
    image: coin.image,
    current_price: coin.current_price,
    price_change_percentage_24h: coin.price_change_percentage_24h,
    formatted_change: formatPercentChange(coin.price_change_percentage_24h)
  }));

  const losers = sortedByGain.slice(-limit).reverse().map(coin => ({
    id: coin.id,
    name: coin.name,
    symbol: coin.symbol.toUpperCase(),
    image: coin.image,
    current_price: coin.current_price,
    price_change_percentage_24h: coin.price_change_percentage_24h,
    formatted_change: formatPercentChange(coin.price_change_percentage_24h)
  }));

  return {
    gainers,
    losers
  };
}

// Get volume leaders (highest 24h trading volumes)
export function getVolumeLeaders(marketData, limit = 5) {
  if (!marketData || marketData.length === 0) {
    return [];
  }

  // Filter out coins with null volumes
  const validData = marketData.filter(coin => 
    coin.total_volume !== null && 
    coin.total_volume !== undefined && 
    coin.total_volume > 0
  );

  // Sort by 24h volume (descending)
  const sortedByVolume = [...validData].sort((a, b) => 
    (b.total_volume || 0) - (a.total_volume || 0)
  );

  return sortedByVolume.slice(0, limit).map(coin => ({
    id: coin.id,
    name: coin.name,
    symbol: coin.symbol.toUpperCase(),
    image: coin.image,
    current_price: coin.current_price,
    total_volume: coin.total_volume,
    formatted_volume: formatVolume(coin.total_volume),
    market_cap: coin.market_cap,
    formatted_market_cap: formatMarketCap(coin.market_cap),
    price_change_percentage_24h: coin.price_change_percentage_24h
  }));
}

// Get market cap leaders (highest market capitalizations)
export function getMarketCapLeaders(marketData, limit = 5) {
  if (!marketData || marketData.length === 0) {
    return [];
  }

  // Filter out coins with null market caps
  const validData = marketData.filter(coin => 
    coin.market_cap !== null && 
    coin.market_cap !== undefined && 
    coin.market_cap > 0
  );

  // Sort by market cap (descending) - should already be sorted from API
  const sortedByMarketCap = [...validData].sort((a, b) => 
    (b.market_cap || 0) - (a.market_cap || 0)
  );

  return sortedByMarketCap.slice(0, limit).map(coin => ({
    id: coin.id,
    name: coin.name,
    symbol: coin.symbol.toUpperCase(),
    image: coin.image,
    current_price: coin.current_price,
    market_cap: coin.market_cap,
    formatted_market_cap: formatMarketCap(coin.market_cap),
    market_cap_rank: coin.market_cap_rank,
    price_change_percentage_24h: coin.price_change_percentage_24h
  }));
}

// Get technical alerts based on trend signals
export function getTechnicalAlerts(coinData) {
  if (!coinData || coinData.length === 0) {
    return [];
  }

  const alerts = [];

  coinData.forEach(coin => {
    if (!coin.trends) return;

    const { weekly, daily, fourHour } = coin.trends;
    
    // Strong bullish alert: All timeframes long
    if (weekly === 'Long' && daily === 'Long' && fourHour === 'Long') {
      alerts.push({
        type: 'bullish',
        severity: 'high',
        coin: {
          id: coin.id,
          name: coin.name,
          symbol: coin.symbol?.toUpperCase() || coin.symbol,
          image: coin.image,
          current_price: coin.current_price
        },
        message: 'All timeframes showing bullish signals',
        timeframes: ['Weekly', 'Daily', '4H']
      });
    }
    
    // Strong bearish alert: All timeframes short
    else if (weekly === 'Short' && daily === 'Short' && fourHour === 'Short') {
      alerts.push({
        type: 'bearish',
        severity: 'high',
        coin: {
          id: coin.id,
          name: coin.name,
          symbol: coin.symbol?.toUpperCase() || coin.symbol,
          image: coin.image,
          current_price: coin.current_price
        },
        message: 'All timeframes showing bearish signals',
        timeframes: ['Weekly', 'Daily', '4H']
      });
    }
    
    // Trend reversal alert: Weekly vs Daily divergence
    else if ((weekly === 'Long' && daily === 'Short') || (weekly === 'Short' && daily === 'Long')) {
      alerts.push({
        type: 'reversal',
        severity: 'medium',
        coin: {
          id: coin.id,
          name: coin.name,
          symbol: coin.symbol?.toUpperCase() || coin.symbol,
          image: coin.image,
          current_price: coin.current_price
        },
        message: 'Weekly and daily trends diverging',
        timeframes: ['Weekly', 'Daily']
      });
    }

    // Momentum alert: Short-term bullish on long-term bullish
    else if (weekly === 'Long' && daily === 'Long' && fourHour !== 'Long') {
      alerts.push({
        type: 'momentum',
        severity: 'medium',
        coin: {
          id: coin.id,
          name: coin.name,
          symbol: coin.symbol?.toUpperCase() || coin.symbol,
          image: coin.image,
          current_price: coin.current_price
        },
        message: 'Strong long-term bullish trend',
        timeframes: ['Weekly', 'Daily']
      });
    }
  });

  // Sort alerts by severity (high first) and limit to most important
  return alerts
    .sort((a, b) => {
      const severityOrder = { high: 3, medium: 2, low: 1 };
      return severityOrder[b.severity] - severityOrder[a.severity];
    })
    .slice(0, 10); // Limit to top 10 alerts
}

// Calculate overall market statistics
export function getMarketStatistics(marketData) {
  if (!marketData || marketData.length === 0) {
    return {
      totalMarketCap: 0,
      totalVolume: 0,
      averageChange: 0,
      bullishCount: 0,
      bearishCount: 0,
      neutralCount: 0
    };
  }

  let totalMarketCap = 0;
  let totalVolume = 0;
  let totalChange = 0;
  let validChangeCount = 0;
  let bullishCount = 0;
  let bearishCount = 0;
  let neutralCount = 0;

  marketData.forEach(coin => {
    // Sum market caps
    if (coin.market_cap) {
      totalMarketCap += coin.market_cap;
    }

    // Sum volumes
    if (coin.total_volume) {
      totalVolume += coin.total_volume;
    }

    // Calculate average price change
    if (coin.price_change_percentage_24h !== null && coin.price_change_percentage_24h !== undefined) {
      totalChange += coin.price_change_percentage_24h;
      validChangeCount++;

      // Count sentiment
      if (coin.price_change_percentage_24h > 1) {
        bullishCount++;
      } else if (coin.price_change_percentage_24h < -1) {
        bearishCount++;
      } else {
        neutralCount++;
      }
    }
  });

  const averageChange = validChangeCount > 0 ? totalChange / validChangeCount : 0;

  return {
    totalMarketCap,
    totalVolume,
    averageChange,
    bullishCount,
    bearishCount,
    neutralCount,
    totalCoins: marketData.length,
    bullishPercent: ((bullishCount / marketData.length) * 100).toFixed(1),
    bearishPercent: ((bearishCount / marketData.length) * 100).toFixed(1)
  };
}

// Get trending insights from external trending data
export function processTrendingData(trendingData) {
  if (!trendingData || !trendingData.coins) {
    return [];
  }

  return trendingData.coins.slice(0, 7).map((trendingCoin, index) => {
    const coin = trendingCoin.item;
    return {
      rank: index + 1,
      id: coin.id,
      name: coin.name,
      symbol: coin.symbol?.toUpperCase() || coin.symbol,
      image: coin.small,
      market_cap_rank: coin.market_cap_rank,
      score: coin.score || 0
    };
  });
}

// Combine all insights for right panel
export function getAllInsights(marketData, trendingData) {
  const movers = getTopMovers(marketData, 5);
  const volumeLeaders = getVolumeLeaders(marketData, 5);
  const marketStats = getMarketStatistics(marketData);
  const trending = processTrendingData(trendingData);

  return {
    topGainers: movers.gainers,
    topLosers: movers.losers,
    volumeLeaders,
    marketStatistics: marketStats,
    trending,
    lastUpdated: Date.now()
  };
}
